import { accountFlowMessageText } from "./identity-account-view.js";
import { element, translate } from "./identity-view-primitives.js";

function hidden(control) {
  const input = element("input");
  input.type = "hidden";
  input.name = control.name;
  input.value = control.value;
  input.disabled = control.disabled;
  return input;
}

function field(control, { optional = false } = {}) {
  const label = element("label", { className: "form-control grid gap-1.5" });
  const copy = control.name === "identifier"
    ? optional
      ? translate("account.field.identifier_optional", "Account handle or recovery email (optional)")
      : translate("account.field.identifier", "Account handle or recovery email")
    : translate("account.field.password", "Password");
  const input = element("input", { className: "input input-sm input-bordered w-full" });
  input.type = control.type;
  input.name = control.name;
  input.value = control.value;
  input.required = optional ? false : control.required;
  input.disabled = control.disabled;
  input.autocomplete = control.autocomplete;
  label.append(element("span", { className: "label-text text-xs", text: copy }), input);
  return label;
}

function messages(nodes) {
  const values = nodes.flatMap((node) => node.messages ?? []);
  if (!values.length) return null;
  const root = element("ul", {
    className: "grid gap-1 rounded-box bg-error/8 px-3 py-2 text-xs text-error",
  });
  root.setAttribute("role", "alert");
  for (const message of values) root.append(element("li", { text: accountFlowMessageText(message) }));
  return root;
}

function loginForm(flow, method) {
  const form = element("form", { className: "grid gap-3" });
  form.action = flow.action;
  form.method = "post";
  form.dataset.accountMenuIdentityForm = method.method;
  for (const control of method.nodes) {
    if (control.type === "hidden") form.append(hidden(control));
    else if (control.name === "identifier") {
      form.append(field(control, { optional: method.method === "passkey" }));
    } else if (control.name === "password") form.append(field(control));
  }
  const nodeMessages = messages(method.nodes);
  if (nodeMessages) form.append(nodeMessages);
  const action = element("button", {
    className: "btn btn-sm w-full",
    text: method.method === "passkey"
      ? translate("account.action.sign_in_passkey", "Sign in with a passkey")
      : translate("account.action.sign_in", "Sign in"),
  });
  if (method.method === "passkey") {
    action.type = "button";
    action.dataset.accountMenuPasskey = "oryPasskeyLogin";
  } else {
    action.type = "submit";
    action.name = "method";
    action.value = "password";
  }
  form.append(action);
  return form;
}

export function createAccountMenuLoginView(flow, { accountUrls }) {
  if (flow?.view !== "login") throw new TypeError("Account menu requires a login flow.");
  const root = element("div", { className: "grid gap-4 px-4 py-4" });
  const header = element("div", { className: "flex items-center justify-between gap-3" });
  header.append(
    element("h2", {
      className: "font-semibold text-base-content",
      text: translate("account.login.title", "Sign in to FishyStuff"),
    }),
  );
  const back = element("button", {
    className: "btn btn-ghost btn-xs",
    text: translate("account.action.back", "Back"),
  });
  back.type = "button";
  back.dataset.accountMenuLoginBack = "";
  header.append(back);
  root.append(header);
  const flowMessages = messages([{ messages: flow.messages }]);
  if (flowMessages) root.append(flowMessages);
  for (const methodName of ["password", "passkey"]) {
    const method = flow.forms.find(({ method: candidate }) => candidate === methodName);
    if (!method) continue;
    if (root.querySelector?.("form")) {
      const divider = element("div", {
        className: "divider my-0 text-xs text-base-content/45",
        text: translate("account.login.or", "or"),
      });
      root.append(divider);
    }
    root.append(loginForm(flow, method));
  }
  const links = element("div", { className: "flex flex-wrap justify-between gap-2 text-xs" });
  const recover = element("a", {
    className: "link link-hover",
    text: translate("account.action.recover", "Recover account"),
  });
  recover.href = accountUrls.recovery;
  const register = element("a", {
    className: "link link-hover",
    text: translate("account.action.create", "Create account"),
  });
  register.href = accountUrls.registration;
  links.append(recover, register);
  root.append(links);
  return root;
}
